import {Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {BaseComponent} from "../../../shared/base.component";
import {Series} from "../../../_models/series";
import {SeriesAuditHistoryTable} from "../series-audit-history.component";
import {Event as WireEvent} from "../../../_models/event";
import {SeriesAuditHistoryService} from "../series-audit-history.service";
import {takeUntil} from "rxjs/operators";
import {IDateTimePeriod} from "../../../_typing/date-time-period";
import {UtilsBarComponent} from "../../../tables/utils-bar/utils.bar.component";
import {COMMON_TABLE_IMPORTS} from "../../../shared/import-groups/Common";
import {FormatNumberPipe, RemoveUnderscorePipe, StringDatePipe, UpperFirstPipe} from "../../../shared/pipes";


interface ISeriesAuditBaseTableConfig {
    type: string;
    start: Date;
    end: Date;
    dtp: IDateTimePeriod
}

export type SeriesAuditBaseTableConfig = Partial<ISeriesAuditBaseTableConfig>

@Component({
    selector: 'series-audit-history-base-table',
    templateUrl: './series-audit-history-base-table.component.html',
    styleUrls: ['./series-audit-history-base-table.component.scss', '../series-audit-history.component.scss'],
    imports: [
        UtilsBarComponent,
        COMMON_TABLE_IMPORTS,
        UpperFirstPipe,
        RemoveUnderscorePipe,
        StringDatePipe,
        FormatNumberPipe
    ],
    encapsulation: ViewEncapsulation.None
})
export class SeriesAuditHistoryBaseTableComponent extends BaseComponent implements OnChanges {
    @Input() series: Series;
    private _config: SeriesAuditBaseTableConfig;
    @Input() set config(data: SeriesAuditBaseTableConfig) {
        this._config = data;
    }

    get config(): SeriesAuditBaseTableConfig {
        return this._config
    }

    dataSource: MatTableDataSource<SeriesAuditHistoryTable>;
    columns = ['timestamp', 'value', 'old_value', 'changed_by', 'changed_on', 'comments'];
    comments: WireEvent[];
    sort;
    pageNumber: number = 0;
    pageSize = 10;
    loading = false;

    @ViewChild(MatPaginator) paginator: MatPaginator;

    @ViewChild(MatSort) set content(content: ElementRef) {
        this.sort = content;
        if (this.sort && this.dataSource) {
            this.dataSource.sort = this.sort;
        }
    }

    constructor(protected cps: SeriesAuditHistoryService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.pageNumber = 0;
        this.dataSource = new MatTableDataSource();
        this.cps.$auditSubHourlyHistoryData.pipe(takeUntil(this.onDestroy))
            .subscribe((result) => {
                this.loading = false;
                this.dataSource.data = result;
                this.setupTable();
            });
        this.cps.$auditHistoryData.pipe(takeUntil(this.onDestroy))
            .subscribe((result) => {
                this.loading = false;
                this.dataSource.data = result;
                this.setupTable();
            });

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.series && this.config) {
            this.loadTableData();
        }
    }

    loadTableData() {
        this.loading = true;
        if (this.config.type === 'sub_hourly') {
            this.cps.getSubHourlySeriesAuditHistory(this.series, this.config.start, this.config.end);
        } else {
            this.cps.onSeriesChanged(this.series, this.config.dtp);
        }
    }

    setupTable() {
        this.dataSource.data.sort((a, b) => {
            return a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0;
        });
        this.dataSource.filterPredicate = (data, filter) => {
            if ((data.changed_by && data.changed_by.toLowerCase().includes(filter))
                || (data.value && data.value.toString().includes(filter))
                || (data.old_value && data.old_value.toString().includes(filter))
            ) {
                return true;
            }
            return false;
        };

        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (data, sortHeaderId) => {
            switch (sortHeaderId) {
                default:
                    return data[sortHeaderId];
                    break;
            }
        };

        this.dataSource.paginator = this.paginator;
    }


    applyFilter(filterValue: any) {
        filterValue = filterValue?.trim().toLowerCase() ?? '';
        this.dataSource.filter = filterValue;
    }

    ngOnDestroy(): void {
        if (this.onDestroy) {
            this.onDestroy.next();
            this.onDestroy.complete();
        }
    }
}
